import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PERMISSIONS, READ, EDIT, CLIENT_PERMISSIONS } from 'shared-permissions';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import usePermissions from 'hooks/usePermissions.jsx';
import useClientPermissions from 'hooks/useClientPermissions.js';
import useCurrentUserAccess from 'hooks/useCurrentUserAccess.js';
import useOrgId from 'hooks/useOrgId.js';

const {
  INTEGRATIONS_ONE_TO_MANY,
  INTEGRATIONS_ONE_TO_ONE,
  VOD_INTEGRATIONS,
  CHAT_SETTINGS,
} = PERMISSIONS;

/**
 * Whether the current user may list the org's product feeds, and create one.
 * The feeds endpoint answers 403 without the read access, so gate
 * useProductFeeds on productFeedsEnabled.
 */
const useProductFeedsAccess = () => {
  const orgId = useOrgId();
  const isSuperadminUser = useSelector((state) => state.sessionState.authUser?.isSuperadmin);
  const masqueradeMode = useSelector((state) => state.ui.masqueradeMode);
  const isSuperadmin = isSuperadminUser && masqueradeMode !== null;
  const { features: { productCatalogEnabled } } = useFeatureContext();
  const { data: currentUserAccess } = useCurrentUserAccess();
  const currentClientId = currentUserAccess?.organizations?.[orgId]?.clientId;
  const { hasClientPermission } = useClientPermissions({ clientId: currentClientId });
  const { hasPermission } = usePermissions();

  return useMemo(() => {
    const hasProductCatalogReadPermission =
      hasClientPermission(CLIENT_PERMISSIONS.MANAGE_PRODUCT_CATALOG_CLIENT, READ) ||
      hasPermission(INTEGRATIONS_ONE_TO_MANY, READ) ||
      hasPermission(INTEGRATIONS_ONE_TO_ONE, READ) ||
      hasPermission(VOD_INTEGRATIONS, READ) ||
      hasPermission(CHAT_SETTINGS, READ);
    const hasProductCatalogEditPermission = hasClientPermission(CLIENT_PERMISSIONS.MANAGE_PRODUCT_CATALOG_CLIENT, EDIT) ||
      hasPermission(INTEGRATIONS_ONE_TO_MANY, EDIT) ||
      hasPermission(INTEGRATIONS_ONE_TO_ONE, EDIT) ||
      hasPermission(VOD_INTEGRATIONS, EDIT) ||
      hasPermission(CHAT_SETTINGS, EDIT);
    const productFeedsEnabled = Boolean(productCatalogEnabled && (hasProductCatalogReadPermission || isSuperadmin));
    const productFeedsCreateEnabled = hasProductCatalogEditPermission || isSuperadmin;
    return { productFeedsEnabled, productFeedsCreateEnabled };
  }, [productCatalogEnabled, isSuperadmin, hasPermission, hasClientPermission]);
};

export default useProductFeedsAccess;
